// RailPicker — single picker that returns BOTH treasury slug AND asset.
//
// Replaces the (asset dropdown + treasury slug picker) pair, which were
// two independent fields that could conflict (e.g. AED + exchange.binance.dxb).
// A "rail" = an existing (slug, asset) combo with a live balance. Picking
// one rail uniquely determines what the operator means.
//
// NO MANUAL DOOR (operator, 14 Sept 2026, sweep item 22). This picker used to
// fall back to a two-field "brand-new rail" entry for a (slug, asset) with no
// balance yet. The chart is closed — an account is never born from a posting —
// so that door only ever led to a refusal, after the operator had typed a slug
// by hand (which is how `cash.ofice` happens). New desk accounts are opened
// from Parties → New → Own account; registered pairs (admin.rail_assets, the
// wallet chain table) are already offered here at zero, so a real new rail is
// in the list before its first deposit.
//
// NAMING + RELEVANCE (2026-08-12 operator pass): every label comes from
// lib/naming (`railName`/`railSubtitle`) — the stored string is never trusted
// and never rendered raw. Ordering and stale-hiding come from the shared
// relevance helpers, so this picker sorts exactly like Treasury, Parties and
// the palette. A search reaches every rail.
//
// MOST USED, THREE OF THEM (operator, 12 Sept 2026: "rail picker filter and
// most used ones appear for the party … show only 3, 1st as most used, 2-3 in
// descending order and with more button"). Unsearched, the list is exactly
// three rails ranked by use — the party's own use of each rail when the form
// has a party (`partySlug`), then the desk's over the last 30 days — and
// everything else waits behind one true-count `More accounts (N)` footer. The
// rule is written once, here, and every screen's picker inherits it; a
// screen only says WHICH party is in context.

import { useState, useRef, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { api, type TreasuryRow, type PartyRail } from '../lib/api';
import { displayAsset, fmtAmount } from '../lib/format';
import { railName, railSubtitle, railKindName, compareRelevance, partitionByRelevance, relevanceTier, railKindGroupLabel, railKindOrder, PRIMARY_RAIL_KINDS, sameAsset, foldAsset } from '../lib/naming';
import { Name } from './Name';
import { DEFAULT_RAILS } from '../lib/workflow';

export interface RailValue {
  slug: string;
  asset: string;
}

/* Group header + section order for a treasury kind come from lib/naming. The
   local KIND_LABEL / KIND_ORDER tables that used to sit here were a second copy
   of that vocabulary and had already drifted — this picker said `Cold storage`
   while Treasury said `Cold wallets` for the identical kind. An unmapped kind is
   sentence-cased by the shared helper, so a slug never reaches the screen, not
   even in a header. */

/** Asset chip + balance, right-aligned. Never concatenated into the primary
 *  label — that is what produced the old `Wio · Shweta · USDT·TRC` mush.
 *
 *  21 Oct 2026 audit: the chip and the figure had no space between them
 *  ("AED741,039.25", "USDT·TRC412,330.12") — now "AED 741,039.25". The chip
 *  stays on EVERY row, a duplicated name included: that is the one place the
 *  asset cannot be cut off (the name beside it truncates, this does not), and an
 *  audit that took the chip off a duplicated name left long duplicate names on a
 *  phone with their "· AED" ellipsised away and two unitless rows (Astra, 21 Oct). */
function RailTrailing({ row, figure = true }: { row: Pick<TreasuryRow, 'asset' | 'balance'>; figure?: boolean }) {
  return (
    <>
      <span className="text-tg-hint text-meta">{displayAsset(row.asset)}</span>
      {/* No figure while the list is unread (readFailed): a failed refresh keeps its old balances, and a balance
          drawn from a read that failed is a claim nobody checked. */}
      {figure && <>{' '}<span className="text-meta">{fmtAmount(row.balance, row.asset)}</span></>}
    </>
  );
}

/** WHAT ONE LIST ROW SAYS (21 Oct 2026 audit), pure so the rules can be pinned
 *  (tests/forms-clean-view). `collides` is whether another row carries the same
 *  name — the operator's 1 Sept rule: a duplicated name gets its asset as the
 *  qualifier ("Office cash · AED"). The chip beside the figure stays as well (see
 *  RailTrailing): the qualifier is on the truncating side of the row.
 *  The line under the name is the kind ONLY where the name does not already say
 *  it ("Office cash / Cash", "Hot wallet · TRC / Wallet", "Cedar Bank / Bank" said
 *  the same word twice on every row), then the state words (Dormant · Quiet ·
 *  Ready — nothing posted yet), which always stay. A kind the name does not
 *  carry — "Bitget / Exchange", "Wio · Shweta / Bank" — is exactly what tells
 *  two similar accounts apart, so it stays.
 *
 *  `unread`: the list could not be refreshed. The name and the kind are what an account IS and stay; the state words
 *  (Dormant · Quiet · Ready — nothing posted yet) are read off its balance and its days of activity, which a failed
 *  read did not confirm — they go with the figures (22 Oct 2026 review). */
export function railRowWords(r: TreasuryRow, collides: boolean, unread = false): { label: string; subtitle: string } {
  const label = collides ? `${railName(r)} · ${foldAsset(r.asset)}` : railName(r);
  const kind = railKindName(r.treasuryKind ?? r.slug?.split('.')[0]);
  const named = !!kind && label.toLowerCase().includes(kind.toLowerCase());
  if (unread) return { label, subtitle: named ? '' : kind };
  return { label, subtitle: railSubtitle(r, { kind: !named }) };
}

/** "usdt-trc", "USDT·TRC", "usdttrc" → "usdttrc": one spelling for the asset match. */
const norm = (x: string) => x.toLowerCase().replace(/[^a-z0-9]/g, '');

export function RailPicker({
  value,
  onChange,
  rows,
  allowedKinds,
  placeholder = 'Pick an account',
  loading,
  readFailed = false,
  partySlug,
  partyEntity = 'dubaiotc',
  hideBalance,
  balanceBelow,
}: {
  value: RailValue;
  onChange: (v: RailValue) => void;
  rows: TreasuryRow[];
  allowedKinds?: string[];      // optional kind filter (for Rebalance same-asset)
  placeholder?: string;
  loading?: boolean;
  /** The accounts list could not be read (the screen passes `query.isError`, so a failed REFRESH over cached rows counts
   *  too: react-query keeps the old data beside the error). Nothing the list says is then drawn as a fact:
   *  - no rows: the opened picker says "Accounts not read — try again." instead of "No such account" (a missing-record
   *    claim, with a way to make a duplicate);
   *  - cached rows: the NAMES stay, so the operator can still pick — but no balance figure (in the trigger or in a row)
   *    and no state word (Dormant · Quiet · Ready); both were read off the balances the failed read did not confirm.
   *  The screen's own line carries the Retry. */
  readFailed?: boolean;
  /** The picked account's balance is already said under the field (Adjust
   *  balance reads it as "holds 741,039.25 → holds 739,789.25"): the trigger
   *  keeps the name and drops the figure, so the balance is shown once. The
   *  open list is unchanged — the balances there are how an account is chosen.
   *  21 Oct 2026 audit. */
  hideBalance?: boolean;
  /** The rebuilt forms (1 Nov 2026, Astra: "Hot walle…" cut beside its balance): the trigger holds the account's name
   *  alone, and it wraps rather than ending in an ellipsis; the balance is a second, muted line under the box — the
   *  figure, then its unit. */
  balanceBelow?: boolean;
  /** The party this form is about, once it is a known one. The picker then
   *  leads with the rails THAT party's money moves through; without it (Move
   *  money, a name still being typed) the desk's most used rails lead. */
  partySlug?: string;
  /** The desk the PARTY belongs to — not the desk whose rails are listed.
   *  Record money lists India's rails for a Dubai party; the party stays a
   *  Dubai party, and its history is matched to rows by account address. */
  partyEntity?: string;
}) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const [showAll, setShowAll] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // THE PARTY'S OWN HABITS, asked for as soon as the party is known so the
  // list is ranked before the picker opens. Nothing is asked without a party.
  // Keyed under 'party' so refreshMoneyViews refetches it after a posting —
  // the rail just used counts from the next open.
  const partyRails = useQuery({
    queryKey: ['party', partySlug ?? '', 'rails', partyEntity],
    queryFn:  () => api.partyRails(partySlug!, partyEntity),
    enabled:  !!partySlug,
    staleTime: 60_000,
  });
  const partyUse = useMemo(() => {
    const m = new Map<string, PartyRail>();
    for (const r of partyRails.data?.rails ?? []) m.set(r.account, r);
    return m;
  }, [partyRails.data]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Every open starts collapsed — "show all" is a per-visit escape hatch, not
  // a preference. Typing also collapses it: search already reaches every tier.
  useEffect(() => { if (!open) { setShowAll(false); setFilter(''); } }, [open]);

  const q = filter.toLowerCase().trim();

  // Filter → relevance-order → split off the stale tail.
  // `lead` says what the unsearched list leads with: 'party' (this party's
  // own habits), 'desk' (the desk's 30-day use), or null (a search, or an
  // older Worker without use counts — the tiered grouping as before).
  const { shown, hidden, lead } = useMemo((): { shown: TreasuryRow[]; hidden: TreasuryRow[]; lead: 'party' | 'desk' | null } => {
    let list = rows;
    if (allowedKinds) {
      const set = new Set(allowedKinds);
      list = list.filter(r => r.treasuryKind && set.has(r.treasuryKind));
    }
    if (q) {
      // A PARTIAL query matches names only: every crypto rail holds USDT, so a
      // substring match on the asset returned the entire list for "usdt"
      // (2026-08-12). A query that IS an asset code — "eur", "usd", "usdt-trc"
      // — matches the asset exactly: the operator typed "eur" with 10,100 EUR
      // sitting on Office cash · EUR and got "No matching accounts" (12 Sept 2026).
      const code = norm(q);
      list = list.filter(r =>
        railName(r).toLowerCase().includes(q) ||
        (r.label ?? '').toLowerCase().includes(q) ||
        (r.slug ?? '').toLowerCase().includes(q) ||
        (r.provider ?? '').toLowerCase().includes(q) ||
        (r.persona ?? '').toLowerCase().includes(q) ||
        (!!r.asset && (code === norm(r.asset) || code === norm(foldAsset(r.asset)) || code === norm(displayAsset(r.asset))))
      );
    }
    // One comparator everywhere: tier → |balance| → recency → name.
    const ordered = [...list].sort(compareRelevance);
    if (q) return { ...partitionByRelevance(ordered, { maxTier: 3 }), lead: null };

    // ── UNSEARCHED: EXACTLY THREE, MOST USED FIRST ──────────────────────────
    // (operator, 12 Sept 2026: "show only 3, 1st as most used, 2-3 in
    // descending order and with more button"). The rank of a rail is the
    // party's own use of it — transactions where the party's account and the
    // rail moved together — when the form has a party and the books know its
    // habits; then the desk's use over the last 30 days, so a party who has
    // only ever used one rail still sees the desk's everyday rails in slots
    // 2–3 rather than a fixed pair; ties by last activity (newest first),
    // then |balance|, then the shared relevance order. A rail with no use on
    // either count is not "used" and never takes a slot by rank.
    //
    // Fewer than three used → the desk defaults fill up, in this order: Office
    // cash · AED, Hot wallet · TRC · USDT — when they are in the list and not
    // already shown. Everything else — the 4th-busiest onward, quiet rails,
    // the cold store, registry-vouched rails nothing has landed on — waits
    // behind "More accounts (N)", busiest first, then the relevance order.
    //
    // Rows from an older Worker carry no `uses_30d`; they take the 30-day
    // activity partition below, unchanged.
    const ranked = list.some((r) => r.uses_30d !== undefined);
    if (ranked) {
      const byParty = !!partySlug && partyUse.size > 0;
      const own  = (r: TreasuryRow) => (byParty ? partyUse.get(r.account)?.uses ?? 0 : 0);
      const desk = (r: TreasuryRow) => r.uses_30d ?? 0;
      const at   = (r: TreasuryRow) => {
        const t = Date.parse(partyUse.get(r.account)?.last_at ?? r.last_activity_at ?? '');
        return Number.isFinite(t) ? t : -Infinity;
      };
      const used = ordered
        .filter((r) => own(r) > 0 || desk(r) > 0)
        .sort((a, b) => own(b) - own(a) || desk(b) - desk(a) || at(b) - at(a) || Math.abs(b.balance) - Math.abs(a.balance));
      const top = used.slice(0, 3);
      for (const d of [DEFAULT_RAILS.cash, DEFAULT_RAILS.hotTrc]) {
        if (top.length >= 3) break;
        const row = ordered.find((r) => r.slug === d.slug && sameAsset(r.asset, d.asset));
        if (row && !top.includes(row)) top.push(row);
      }
      const shownSet = new Set(top), usedSet = new Set(used);
      return {
        shown:  top,
        hidden: [...used.slice(3), ...ordered.filter((r) => !shownSet.has(r) && !usedSet.has(r))],
        lead:   byParty ? 'party' : 'desk',
      };
    }

    // UNSEARCHED, THE LIST IS THE RAILS THE DESK USES: a rail that moved in
    // the last 30 days (or is pinned). A funded cold store that has not moved
    // — My Ledger's TRX, GRAM, APT — sits behind "More rails" with the rest
    // (operator, 12 Sept 2026: "so many options still visible … only frequent
    // rails should stay visible"). A search reaches everything. Without
    // activity data (an older Worker) the balance decides, as before.
    const byTier = (() => {
          const shown: TreasuryRow[] = [], hidden: TreasuryRow[] = [];
          for (const r of ordered) {
            const tier = relevanceTier(r);
            const idle = r.last_activity_at !== undefined && r.last_activity_at !== null
              && (Date.now() - Date.parse(r.last_activity_at)) / 86400000 > 30;
            const inUse = tier === 0 || (tier === 1 && !idle && r.last_activity_at !== undefined);
            const fallback = r.last_activity_at === undefined && tier <= 2;   // older Worker: as before
            (inUse || fallback ? shown : hidden).push(r);
          }
          return { shown, hidden };
        })();

    // ── LEAD WITH WHERE THE MONEY ACTUALLY ARRIVES ──────────────────────────
    // Wallets and cash are the 95% case; 40 bank rails, 9 exchanges and 20
    // legacy husks are the tail. Unsearched, the tail joins the SAME footer the
    // stale tier already uses — one "More" affordance, never two, and nothing
    // is removed from the list. A search reaches everything, because by then
    // the operator has said what they are looking for.
    const isPrimary = (r: TreasuryRow) =>
      PRIMARY_RAIL_KINDS.has((r.treasuryKind ?? r.slug?.split('.')[0] ?? '').toLowerCase());
    return {
      shown:  byTier.shown.filter(isPrimary),
      hidden: [...byTier.shown.filter((r) => !isPrimary(r)), ...byTier.hidden],
      lead:   null,
    };
  }, [rows, allowedKinds, q, partySlug, partyUse]);

  const grouped: Record<string, TreasuryRow[]> = {};
  for (const r of shown) {
    const k = r.treasuryKind ?? 'other';
    if (!grouped[k]) grouped[k] = [];
    grouped[k]!.push(r);
  }
  // DUPLICATE LABELS GET THE ASSET (operator, 1 Sept: three identical
  // "Hot wallet · ERC" rows are not tidy). The no-asset-in-name rule stands
  // for unique labels; a COLLISION is exactly what the grammar's qualifier
  // slot exists for, and the asset is the only true qualifier here.
  const labelCount: Record<string, number> = {};
  for (const r of [...shown, ...hidden]) {
    const k = railName(r);
    labelCount[k] = (labelCount[k] ?? 0) + 1;
  }
  const rowWords = (r: TreasuryRow) => railRowWords(r, (labelCount[railName(r)] ?? 0) > 1, readFailed);
  const kinds = Object.keys(grouped).sort((a, b) => railKindOrder(a) - railKindOrder(b));
  // A RANKED LIST IS ONE SECTION, IN RANK ORDER. Grouping the three by kind
  // would put a bank above the wallet the party uses most — the order IS the
  // information ("1st as most used, 2-3 in descending order"). A search and
  // the older-Worker fallback keep the kind sections.
  const sections: Array<{ key: string; label: string; rows: TreasuryRow[] }> = lead
    ? [{ key: 'used', label: lead === 'party' ? 'Most used with this party' : 'Most used', rows: shown }]
    : kinds.map((k) => ({ key: k, label: railKindGroupLabel(k), rows: grouped[k]! }));
  // "No matching accounts" means no accounts at all — a collapsed stale tail
  // is not an empty result, it is a footer waiting to be opened.
  const nothingToShow = shown.length === 0 && hidden.length === 0;

  // Selected-state display: the matched row drives the label; a value with no
  // matching row (a prefill from a link, a list still loading) is still
  // rendered through railName() (structural row, so the grammar applies to
  // any slug — the UI never trusts a raw slug).
  // sameAsset, never ===: the chain feeds and the v2 dialect say USDTTRC,
  // books-v3 says USDT — a raw comparison rendered a funded wallet as 0.00
  // (operator, 1 Sept). The fold is the project-wide naming contract.
  const matchedRow = useMemo(
    () => rows.find((r) => r.slug === value.slug && sameAsset(r.asset, value.asset)),
    [rows, value.slug, value.asset],
  );
  const selected: TreasuryRow | null = value.slug && value.asset
    ? matchedRow ?? { account: value.slug, slug: value.slug, asset: value.asset, balance: 0 }
    : null;

  const pick = (r: TreasuryRow) => {
    onChange({ slug: r.slug!, asset: r.asset! });
    setOpen(false);
    setFilter('');
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="input w-full flex justify-between items-center text-left gap-2"
      >
        {selected && balanceBelow ? (
          <span className="flex-1 min-w-0 whitespace-normal break-words py-[2px] lg:py-px text-body">{railName(selected)}</span>
        ) : selected ? (
          // No secondary line here on purpose: the trigger is a form control
          // and must keep the exact height of every other .input beside it.
          // The asset + balance ride in `trailing`, never glued onto the name.
          // `[&_*]:min-h-0` defuses the row-height reservation <Name/> uses to
          // stop lists jittering: a list wants the reserved second line, a
          // form control must not grow one.
          // 21 Oct 2026 audit (Move money's From / To): a picked trigger stood
          // 34px beside 40px for an empty one and for every .input around it —
          // Name's own line is 18px, an .input's line box 24px. The 3px above and
          // below bring the picked state to the same height as the rest.
          <span className="flex-1 min-w-0 py-[2px] lg:py-px [&_*]:min-h-0">
            <Name
              primary={railName(selected)}
              trailing={hideBalance ? undefined : matchedRow ? <RailTrailing row={matchedRow} figure={!readFailed} /> : displayAsset(selected.asset)}
            />
          </span>
        ) : (
          <span className="flex-1 min-w-0 truncate text-tg-hint">{placeholder}</span>
        )}
        {open ? <ChevronDown size={14} className="shrink-0" /> : <ChevronRight size={14} className="shrink-0" />}
      </button>
      {balanceBelow && selected && !hideBalance && matchedRow && !readFailed && (
        // A labelled figure, not a bare one (1 Nov 2026 audit: "741,039,250 AED" under a picker said nothing about what it was):
        // "Balance" at the left, the figure mono at the right, its unit muted after it — the same row as BalanceRows.
        <p className="mt-1 flex items-baseline justify-between gap-3 text-body">
          <span className="text-meta text-tg-hint">Balance</span>
          <span className="whitespace-nowrap"><span className="font-mono tabular">{fmtAmount(matchedRow.balance, matchedRow.asset)}</span>&nbsp;<span className="text-meta text-tg-hint">{displayAsset(matchedRow.asset)}</span></span>
        </p>
      )}

      {open && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-tg-section-bg rounded-lg shadow-lg max-h-80 overflow-y-auto border border-tg-secondary">
          <div className="p-2 sticky top-0 bg-tg-section-bg border-b border-tg-secondary">
            <input
              autoFocus
              className="input w-full"
              placeholder="Search"
              value={filter}
              onChange={(e) => { setFilter(e.target.value); setShowAll(false); }}
            />
          </div>

          {loading && <p className="text-tg-hint text-meta px-3 py-2">…</p>}

          {/* No account, no door: the chart is closed, so the only next move is
              to open the account where accounts are opened (14 Sept 2026). A
              search that finds nothing says so in the picker's own words. */}
          {!loading && nothingToShow && (
            <p className="text-tg-hint text-meta px-3 py-3 text-center">
              {readFailed ? 'Accounts not read — try again.' : q ? 'No matching accounts' : (
                <>
                  No such account. New accounts are opened from{' '}
                  <Link to="/parties/new" className="btn-link">Parties → New → Own account</Link>.
                </>
              )}
            </p>
          )}

          {!loading && sections.map((s) => (
            <div key={s.key}>
              <p className="text-tg-hint text-meta px-3 pt-2 pb-1">
                {s.label}
              </p>
              {s.rows.map(r => (
                <button
                  key={`${r.slug}-${r.asset}`}
                  type="button"
                  onMouseDown={() => pick(r)}
                  className="w-full text-left px-3 py-1.5 hover:bg-tg-secondary"
                >
                  <Name
                    primary={rowWords(r).label}
                    secondary={rowWords(r).subtitle}
                    trailing={<RailTrailing row={r} figure={!readFailed} />}
                  />
                </button>
              ))}
            </div>
          ))}

          {/* Neither the tail kinds nor tier 3 ever disappear — one footer, a true
              count, and everything is one click away. */}
          {!loading && hidden.length > 0 && !showAll && (
            <button
              type="button"
              onClick={() => setShowAll(true)}
              className="w-full border-t border-tg-secondary px-3 py-2 text-left text-meta text-tg-hint hover:bg-tg-secondary"
            >
              More accounts ({hidden.length})
            </button>
          )}
          {!loading && hidden.length > 0 && showAll && (
            <div>
              <p className="text-tg-hint text-meta px-3 pt-2 pb-1 border-t border-tg-secondary">
                {/* Behind a ranked three the tail holds used rails too (the
                    4th-busiest onward), so it cannot be called quiet. */}
                {lead ? 'Every other account, most used first' : 'Exchanges, banks and quiet accounts'}
              </p>
              {hidden.map(r => (
                <button
                  key={`stale-${r.slug}-${r.asset}`}
                  type="button"
                  onMouseDown={() => pick(r)}
                  className="w-full text-left px-3 py-1.5 opacity-70 hover:opacity-100 hover:bg-tg-secondary"
                >
                  <Name
                    primary={rowWords(r).label}
                    secondary={rowWords(r).subtitle}
                    trailing={<RailTrailing row={r} figure={!readFailed} />}
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
